import { Config } from "@config";
import { ResponsiveHelper } from "@utils/responsive-helper";
import autobind from "autobind-decorator";
import { Cookie, __ } from "bm-ui-kit";
import compact from "lodash/compact";
import React from "react";
import type { ReactNode } from "react";

declare global {
	interface Window {
		goAdverticum3: any;
		goa3onReady: any;
	}
}

interface BannerInfo {
	banner: number;
	goal: number;
	zone: number;
}

interface Props {
	name: string;
	code: string;
	type: "all" | "desktop" | "non-desktop";
	width: string;
	height: string;
	hasAd?: boolean;
	gapeless?: boolean;
	priority?: number;
	withoutLabel?: boolean;
	onLoaded?: (hasBanner: boolean) => void;
}

interface State {
	need: boolean;
}

const POOL_TIME = 250;
const IS_DEBUG = Cookie.get("bm-debug", "false") === "true";

/**
 * @Shortcode
 *
 * @title "Reklám felület"
 * @shortcode "ad"
 * @type "widget"
 * @importMode "eager"
 */
export class Ad extends React.Component<Props, State> {
	private readonly outerClassName: string;
	private static pool: { zone: string; priority: number; onLoaded: (banner: number) => void }[] = [];
	private static poolTimer: number;
	private static startTime = undefined;
	private static goa3;

	public constructor(props: Props) {
		super(props);

		if (!Ad.startTime) {
			Ad.startTime = new Date().getTime();
		}

		this.outerClassName = compact([
			"adzone text-center bg-light",
			!props.withoutLabel && "p-2",
			props.type === "desktop" && "d-lg-block d-none",
			props.type === "non-desktop" && "d-lg-none",
		]).join(" ");

		const { type } = props;

		if (!Config.isSsr()) {
			if (IS_DEBUG) {
				this.state = { need: false };

				setTimeout(() => {
					this.setState({ need: true });
				}, 10);

				return;
			}

			if (ResponsiveHelper.mediaBreakPointDown("lg")) {
				if (type === "desktop") {
					this.state = { need: false };

					return;
				}
			} else if (type === "non-desktop") {
				this.state = { need: false };

				return;
			}

			Ad.addPool(props.code, props.priority, this.onLoaded);
		}

		this.state = { need: true };
	}

	public static scriptLoaded(goa3: any): void {
		if (Ad.goa3) {
			return;
		}

		Ad.goa3 = goa3;

		if (Ad.poolTimer) {
			window.clearTimeout(Ad.poolTimer);
		}

		Ad.startPool();
	}

	private static addPool(zone: string, priority: number, onLoaded: (banner: number) => void): void {
		Ad.pool.push({ zone, priority, onLoaded });

		if (!Ad.startTime) {
			Ad.startTime = new Date().getTime();
		}

		if (Ad.goa3) {
			if (Ad.poolTimer) {
				window.clearTimeout(Ad.poolTimer);
			}

			Ad.poolTimer = window.setTimeout(() => {
				Ad.startPool();
			}, POOL_TIME);
		}
	}

	private static startPool(): void {
		const { pool } = Ad;
		Ad.pool = [];

		if (!Ad.goa3) {
			return;
		}

		Ad.goa3.invocation(
			pool.map((data) => ({
				node: document.getElementById(`ad${data.zone}`),
				id: data.zone,
				p: data.priority,
			})),
			(response: BannerInfo[]) => {
				pool.forEach((item) => {
					const info = response.find((i) => item.zone === `${i.zone}`);
					item.onLoaded(info?.banner);
				});

				Ad.startTime = undefined;
			},
		);
	}

	@autobind
	private onLoaded(banner: number): void {
		const { code, hasAd, onLoaded } = this.props;

		if (banner) {
			if (!hasAd) {
				const parentElement = document.getElementById(`ad${code}`)?.parentElement;

				if (parentElement) {
					parentElement.style.position = "initial";
					parentElement.style.top = "initial";
					parentElement.style.left = "initial";
				}
			}
		} else {
			this.setState({
				need: false,
			});
		}

		if (onLoaded) {
			onLoaded(!!banner);
		}
	}

	public render(): ReactNode {
		const { code, width, height, name, hasAd, gapeless, withoutLabel } = this.props;
		const { need } = this.state;
		const text = __("Hirdetés");

		if (!need) {
			return (
				<div className="d-none" style={{ width: `${width}px`, height: `${height}px` }}>
					<div id={`ad${code}`} className="goAdverticum" />
				</div>
			);
		}

		if (IS_DEBUG) {
			return (
				<div className={compact([this.outerClassName, !gapeless && "my-2"]).join(" ")}>
					<div className="mx-auto goAdverticum" style={{ width: `${width}px`, height: `${height}px` }}>
						<div className="bg-info text-center text-white h-100 flex-column d-flex flex-wrap justify-content-center align-items-center">
							<span className="h3 text-dark">#{code}</span>
							<span className="h2">{name}</span>
							<small>({`${width}x${height}`})</small>
						</div>
					</div>
					{!withoutLabel && <small className="text-muted">{text}</small>}
				</div>
			);
		}

		return (
			<div
				className={compact([this.outerClassName, !gapeless && "my-2"]).join(" ")}
				style={{
					position: hasAd ? undefined : "absolute",
					top: hasAd ? undefined : "-9999px",
					left: hasAd ? undefined : "-9999px",
				}}
			>
				<div
					id={`ad${code}`}
					className="mx-auto goAdverticum"
					style={{
						width: `${width}px`,
						height: `${height}px`,
					}}
				/>
				<small className="text-muted">{text}</small>
			</div>
		);
	}
}

window.goa3onReady = Ad.scriptLoaded;

if (window.goAdverticum3) {
	window.goAdverticum3.onReady(window.goa3onReady);
}

export default Ad;
